import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Check,
  CodeXml,
  CreditCard,
  KeyRound,
  MessageSquare,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { useQuery } from "@tanstack/react-query";

import { cn } from "@/lib/utils";
import { StandardPageLayout } from "components/layouts/standard-page-layout";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { Spinner } from "components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { fetchApiKeys, fetchSubscription } from "data/queries-common";
import * as Routes from "routes";
import { resolveIdentityId } from "utils/identity";

const steps = [
  {
    title: "Choose a subscription",
    description: "Your organisation needs a subscription before you can create apps. Compare plans for the Geoscape Verify modules and features your integration needs.",
    action: "View subscription plans",
    href: Routes.orgManageSubscription,
    icon: CreditCard,
  },
  {
    title: "Create an app and get your API key",
    description: "Open My Apps, and click Create App to create an API key. Make sure you have enabled access to Geoscape Verify. Your app’s API key (under Credentials) authenticates your Verify requests. Keep that key private.",
    action: "Go to My Apps",
    href: Routes.developerApiKeys,
    icon: KeyRound,
  },
  {
    title: "Explore the Verify features",
    description: "Choose the features your application needs: Address Verify, MailPoint Verify, Phone Verify or Email Verify. Read the Geoscape Verify API docs to understand and evaluate the request parameters and verification results for each feature.",
    action: "Open Verify API docs",
    href: Routes.verifyApiDocs,
    icon: BookOpen,
  },
  {
    title: "Send your first request",
    description: "Follow the Verify quickstart with your app’s API key to send an address verification request. Check the verification result, then make the same request from your application. Use the features docs to add MailPoint, phone or email verification as needed.",
    action: "Open Verify quickstart",
    href: Routes.verifyQuickstartDocs,
    icon: CodeXml,
  },
];

export const VerifyGetStartedPage = () => {
  const { show } = useIntercom();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
    enabled: !!identityId,
  });
  const appsQuery = useQuery({
    queryKey: [QueryKeys.apiKeys, identityId],
    queryFn: () => fetchApiKeys(),
    enabled: !!identityId,
  });
  const stepProgress = [
    {
      completed:
        subscriptionQuery.isSuccess &&
        !!subscriptionQuery.data.activeSubscription,
      checking: subscriptionQuery.isPending,
    },
    {
      completed: appsQuery.isSuccess && appsQuery.data.length > 0,
      checking: appsQuery.isPending,
    },
  ];

  return (
    <StandardPageLayout
      maxWidth="6xl"
      pageTitle={{
        icon: Check,
        title: "Get started with Geoscape Verify",
        description: "From your Hub account to your first Geoscape Verify API request.",
      }}
    >
      <ReactQueryErrorWrapper queries={[subscriptionQuery, appsQuery]} />
      <Card className="gap-0 overflow-hidden p-0">
        <div className="border-b bg-primary/5 p-6 sm:p-8">
          <Badge variant="secondary" className="mb-4">
            Integration guide
          </Badge>
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Bring Geoscape Verify into your application.
          </h2>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Start with a subscription, create an app, then explore the Verify
            modules and send your first verification
            request. Already have a Hub subscription or app? Pick up at the step
            you need.
          </p>
        </div>

        <ol className="divide-y">
          {steps.map(({ title, description, action, href, icon: Icon }, index) => {
            const external = href.startsWith("https://");
            const completed = stepProgress[index]?.completed ?? false;
            const checking = stepProgress[index]?.checking ?? false;
            return (
              <li key={title} className="flex gap-4 p-6 sm:gap-6 sm:p-8">
                <span
                  className={cn(
                    "grid size-9 shrink-0 place-items-center rounded-full border text-sm font-semibold",
                    completed
                      ? "border-success/30 bg-success/10 text-success"
                      : "bg-muted",
                  )}
                  aria-hidden="true"
                >
                  {completed ? (
                    <Check className="size-5" />
                  ) : checking ? (
                    <Spinner className="size-4" />
                  ) : (
                    index + 1
                  )}
                </span>
                <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-6">
                  <div className="flex-1">
                    <h3 className="flex items-center gap-2 text-base font-semibold">
                      <Icon
                        className="hidden size-4 shrink-0 text-primary sm:block"
                        aria-hidden="true"
                      />
                      {title}
                      {completed && (
                        <span className="sr-only"> — completed</span>
                      )}
                      {checking && (
                        <span className="sr-only"> — checking completion</span>
                      )}
                    </h3>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                      {description}
                    </p>
                  </div>
                  <Button
                    asChild
                    variant={index === 0 ? "default" : "outline"}
                    className="mt-4 sm:mt-0 sm:w-56 sm:shrink-0"
                  >
                    {external ? (
                      <a href={href} target="_blank" rel="noopener noreferrer">
                        {action}
                        <ArrowUpRight aria-hidden="true" />
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    ) : (
                      <Link to={href}>
                        {action}
                        <ArrowRight aria-hidden="true" />
                      </Link>
                    )}
                  </Button>
                </div>
              </li>
            );
          })}
        </ol>
      </Card>

      <div className="flex flex-col gap-4 rounded-xl border p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold">
            Need a hand getting started?
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Talk to our team about Verify features, subscriptions or your integration.
          </p>
        </div>
        <Button variant="outline" onClick={() => show()}>
          <MessageSquare aria-hidden="true" />
          Chat with our team
        </Button>
      </div>
    </StandardPageLayout>
  );
};
